/**
 * Install the extension: the page every invitation points at.
 *
 * Behind sign-in (App.tsx wraps it in `RequireAccount`), because the download
 * is: the access list decides who gets the extension, not who has the URL. An
 * invited person arrives from the email, signs in, and comes straight back
 * here.
 *
 * The steps are not written here — they come from `GET /api/extension`, the
 * same `installSteps` the invitation email quotes, so what the email said and
 * what this page says cannot drift apart.
 */

import type { ReactNode } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { Link } from "react-router";
import type { InstallStep } from "@xtract-bot/embellisher-schema";
import { api } from "../api";
import { CopyButton, ErrorAlert, Skeleton, SkeletonRows, formatAbsolute } from "../components/ui";
import { useAsync } from "../useAsync";
import { useSession } from "../useSession";

/** Marks up the parts of a step that are something to type or paste. */
function StepText({ text }: { text: string }): ReactNode {
  const parts = text.split(/(https?:\/\/[^\s,)]+[^\s,).]|chrome:\/\/extensions)/g);
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <code key={index} className="emb-url">
            {part}
          </code>
        ) : (
          part
        ),
      )}
    </>
  );
}

function Step({ step, index, done }: { step: InstallStep; index: number; done: boolean }): ReactNode {
  return (
    <li className="d-flex gap-3 py-3 border-bottom border-light-subtle">
      <span
        className={`flex-shrink-0 rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold ${done ? "bg-success text-white" : "emb-sunken"}`}
        style={{ width: "2rem", height: "2rem" }}
        aria-hidden="true"
      >
        {done ? "✓" : index + 1}
      </span>
      <div>
        <div className="fw-semibold d-flex align-items-center gap-2">
          {step.title}
          {done ? <Badge bg="success">done</Badge> : null}
        </div>
        <div className="emb-muted">
          <StepText text={step.text} />
        </div>
        {step.substeps?.length ? (
          <ol type="a" className="emb-muted mb-0 mt-2 ps-3 d-flex flex-column gap-1">
            {step.substeps.map((substep) => (
              <li key={substep}>
                <StepText text={substep} />
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </li>
  );
}

export function Install(): ReactNode {
  const { account } = useSession();
  const info = useAsync(() => api.extension(), []);
  const latest = info.data?.latest ?? null;

  return (
    <Container style={{ maxWidth: "60rem" }}>
      <p className="emb-eyebrow mb-2">Install</p>
      <h1 className="h3 mb-2">Install the extension</h1>
      <p className="emb-muted mb-4" style={{ maxWidth: "44rem" }}>
        You are signed in{account ? ` as ${account.username}` : ""}, so the download is yours. Loading it takes a couple of
        minutes; then link it and connect your assistant.
      </p>

      {info.error ? (
        <ErrorAlert error={info.error} onRetry={() => void info.reload()} />
      ) : (
        <Row className="g-4">
          <Col lg={8}>
            <Card className="mb-4">
              <Card.Body>
                {info.loading ? (
                  <Skeleton height="4rem" />
                ) : latest && info.data?.downloadUrl ? (
                  <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
                    <div className="flex-grow-1">
                      <div className="fw-semibold">embellisher-{latest.version}.zip</div>
                      <div className="emb-muted small">
                        Version {latest.version} · {(latest.sizeBytes / 1024).toFixed(0)} kB · published {formatAbsolute(latest.uploadedAt)}
                      </div>
                    </div>
                    {/* A plain link, not fetch: the browser saves the file, and the cookie goes with it. */}
                    <Button href={info.data.downloadUrl} variant="primary" size="lg">
                      Download
                    </Button>
                  </div>
                ) : (
                  <Alert variant="warning" className="mb-0">
                    <p className="fw-semibold mb-1">No extension build has been published here yet.</p>
                    <p className="mb-0 small">
                      {account?.isSuperAdmin ? (
                        <>
                          Publish one from <Link to="/admin">Admin → Invitations</Link>, or run{" "}
                          <code>pnpm --filter @xtract-bot/embellisher-extension extension:publish --wrangler</code>.
                        </>
                      ) : (
                        "Whoever runs this deployment publishes it. Ask them, or check back later — this page always offers the newest one."
                      )}
                    </p>
                  </Alert>
                )}
                {latest?.sha256 ? (
                  <p className="emb-muted small mb-0 mt-3 text-break">
                    SHA-256 <code className="emb-mono">{latest.sha256}</code>
                  </p>
                ) : null}
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>Step by step</Card.Header>
              <Card.Body className="py-0">
                {info.loading || !info.data ? (
                  <div className="py-3">
                    <SkeletonRows rows={6} />
                  </div>
                ) : (
                  <ol className="list-unstyled mb-0">
                    {info.data.steps.map((step, index) => (
                      // The first step is signing in, which anyone reading this has done.
                      <Step key={step.title} step={step} index={index} done={index === 0} />
                    ))}
                  </ol>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="mb-4">
              <Card.Body>
                <h2 className="h6 mb-2">Your MCP server URL</h2>
                {info.data ? (
                  <div className="d-flex align-items-center gap-2 emb-sunken px-3 py-2">
                    <code className="emb-url flex-grow-1 text-break">{info.data.mcpUrl}</code>
                    <CopyButton value={info.data.mcpUrl} />
                  </div>
                ) : (
                  <Skeleton height="2.6rem" />
                )}
                <p className="emb-muted small mb-0 mt-2">
                  What your assistant connects to. <Link to="/connect">Steps for each assistant</Link>.
                </p>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Body>
                <h2 className="h6 mb-2">Updating</h2>
                <p className="emb-muted small mb-0">{info.data?.updateNote ?? <Skeleton height="4rem" />}</p>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <h2 className="h6 mb-2">Why not the Chrome Web Store?</h2>
                <p className="emb-muted small mb-0">
                  This build can run logic modules — small compiled programs your assistant writes — which the store's
                  policy on remotely-supplied code does not allow. Loading it unpacked is Chrome's supported way to run
                  an extension like this; the developer-mode notice is Chrome saying exactly that.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}
